<template>
  <div id="app" class="app-safe-area min-h-[100dvh] mentor-app-shell relative overflow-hidden">
    <div class="mentor-paper-plane pointer-events-none absolute inset-0 -z-10"></div>
    
    <!-- Main content -->
    <div class="relative z-10">
      <div v-if="bootstrapping" class="flex min-h-[100dvh] flex-col items-center justify-center gap-3 px-6 text-center" data-testid="session-restore">
        <svg class="h-8 w-8 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
        <p class="text-sm font-semibold text-slate-700">Restoring your session…</p>
        <p class="text-xs text-slate-500">Saved lists stay visible while we refresh.</p>
      </div>
      <RouterView v-else />
    </div>

    <MindGuide />

    <ForceUpdateOverlay />

    <!-- Global Toast Notifications -->
    <ToastContainer />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { RouterView } from 'vue-router'
import MindGuide from '@/components/common/MindGuide.vue'
import ForceUpdateOverlay from '@/components/common/ForceUpdateOverlay.vue'
import ToastContainer from '@/components/common/ToastContainer.vue'
import { useAuthStore } from '@/stores/auth'
import { perfMark, perfMeasure } from '@/utils/perfMarks'

const authStore = useAuthStore()
const bootstrapping = ref(true)

onMounted(async () => {
  perfMark('auth-init-start')
  try {
    await authStore.initializeAuth()
  } finally {
    perfMark('auth-init-done')
    perfMeasure('auth-init', 'auth-init-start', 'auth-init-done')
    perfMeasure('boot-to-route', 'boot-start', 'auth-init-done')
    bootstrapping.value = false
  }
})
</script>

<style scoped>
.mentor-app-shell {
  background:
    linear-gradient(120deg, rgba(245, 192, 78, 0.08), rgba(119, 214, 201, 0.12) 38%, rgba(96, 73, 232, 0.08)),
    #f7f9fc;
  background-attachment: scroll;
}

.mentor-paper-plane {
  background:
    linear-gradient(rgba(12, 31, 59, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(12, 31, 59, 0.025) 1px, transparent 1px),
    radial-gradient(circle at 18% 14%, rgba(245, 192, 78, 0.14), transparent 28%),
    radial-gradient(circle at 85% 22%, rgba(119, 214, 201, 0.16), transparent 30%);
  background-size: 34px 34px, 34px 34px, auto, auto;
  mask-image: linear-gradient(180deg, black 0%, black 82%, transparent 100%);
}

/* Custom shimmer effect for premium feel */
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.shimmer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  animation: shimmer 2s infinite;
}
</style>
